import * as React from 'react'

import { useDividendEvent } from './hooks'
import { path } from '../../../../libs'
import {
  formatAmount,
  formatDate,
  formatPercent,
  statusColor,
} from '../utils'
import {
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd'
import {
  CalendarOutlined,
  DollarOutlined,
  EditOutlined,
  ProfileOutlined,
} from '@ant-design/icons'

const { Text, Title } = Typography

const panelStyle: React.CSSProperties = {
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
}

const metricCardStyle: React.CSSProperties = {
  ...panelStyle,
  height: '100%',
}

const loadingContainerStyle: React.CSSProperties = {
  minHeight: 360,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const DividendEventDetail = () => {
  const { dividendEvent, loading } = useDividendEvent()
  const isLoading = loading || !dividendEvent.dividendEventID
  const appreciationDividendInfo = dividendEvent.appreciationDividendInfo
  const isCompleted = dividendEvent.status === 'completed'
  const isPropertyAppreciation = dividendEvent.type === 'propertyAppreciation'
  const currentStatus = dividendEvent.status || 'unknown'
  const currentType = dividendEvent.type || 'type N/A'
  const amountGross = Number(dividendEvent.amount)
  const feeTotal = Number(dividendEvent.feeTotal)
  const amountNet =
    dividendEvent.amount !== '' &&
    dividendEvent.feeTotal !== '' &&
    Number.isFinite(amountGross) &&
    Number.isFinite(feeTotal)
      ? amountGross * (1 - feeTotal / 100)
      : undefined

  if (isLoading) {
    return (
      <div style={loadingContainerStyle}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: 24 }}>
      <div
        style={{
          background: 'var(--crm-bg)',
          borderBottom: '1px solid var(--crm-border)',
          padding: '16px 20px',
          margin: '0 -12px 16px',
        }}
      >
        <Flex align="flex-start" justify="space-between" gap={16} wrap="wrap">
          <Space align="start" size={14}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                background: '#e6f4ff',
                color: '#1677ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
              }}
            >
              <ProfileOutlined />
            </div>
            <div>
              <Flex align="center" gap={8} wrap="wrap">
                <Title level={5} style={{ margin: 0, fontSize: 18 }}>
                  Dividend event {dividendEvent.dividendEventID || '-'}
                </Title>
                {isCompleted && <Tag color="red">Locked</Tag>}
              </Flex>
              <Space size={[10, 4]} wrap style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {dividendEvent.name || '-'} / Property ID:{' '}
                  {dividendEvent.propertyId || '-'}
                </Text>
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Status:
                  </Text>
                  <Tag
                    color={statusColor(dividendEvent.status)}
                    style={{ margin: 0 }}
                  >
                    {currentStatus}
                  </Tag>
                </Space>
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Type:
                  </Text>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {currentType}
                  </Tag>
                </Space>
              </Space>
            </div>
          </Space>

          <Space size={8} wrap>
            <Button
              type="primary"
              icon={<EditOutlined />}
              disabled={isCompleted}
              href={path.nova.dividendEvents.edit(
                dividendEvent.dividendEventID,
              )}
            >
              Edit
            </Button>
          </Space>
        </Flex>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Amount Gross"
              value={
                isPropertyAppreciation
                  ? appreciationDividendInfo?.finalPropertyPrice
                  : formatAmount(dividendEvent.amount)
              }
              prefix={<DollarOutlined />}
              suffix="USD"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Amount Net"
              value={
                isPropertyAppreciation
                  ? appreciationDividendInfo?.totalNetValue
                  : formatAmount(amountNet)
              }
              prefix={<DollarOutlined />}
              suffix="USD"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Fee Total"
              value={
                isPropertyAppreciation
                  ? appreciationDividendInfo?.appreciationFeePercent
                  : formatAmount(dividendEvent.feeTotal)
              }
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Payment Date"
              value={formatDate(dividendEvent.paymentDate)}
              prefix={<CalendarOutlined />}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Event overview"
        style={panelStyle}
        extra={
          <Tag color="geekblue">
            Stage {dividendEvent.stage} / {dividendEvent.stages}
          </Tag>
        }
      >
        <Descriptions size="small" column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label="Property ID">
            {dividendEvent.propertyId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            {dividendEvent.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Token amount">
            {dividendEvent.tokenAmount || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Property price">
            {dividendEvent.propertyPrice || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Investment strategy">
            {dividendEvent.investmentStrategy.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Stage">
            {dividendEvent.stage} / {dividendEvent.stages}
          </Descriptions.Item>
          <Descriptions.Item label="Amount">
            {formatAmount(dividendEvent.amount)} USD
          </Descriptions.Item>
          <Descriptions.Item label="Payment date">
            {formatDate(dividendEvent.paymentDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Annual dividends">
            {formatPercent(dividendEvent.annualDividends)}
          </Descriptions.Item>
          <Descriptions.Item label="Fee total">
            {formatPercent(dividendEvent.feeTotal)}
          </Descriptions.Item>
          {isPropertyAppreciation && (
            <>
              <Descriptions.Item label="Initial property price">
                {appreciationDividendInfo?.initialPropertyPrice ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Final property price">
                {appreciationDividendInfo?.finalPropertyPrice ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Final property appreciation">
                {appreciationDividendInfo?.finalPropertyAppreciation ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Appreciation fee">
                {appreciationDividendInfo?.appreciationFeeValue
                  ? `${formatPercent(
                      appreciationDividendInfo.appreciationFeePercent,
                    )} of ${appreciationDividendInfo.appreciationFeeValue} USD`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Total NET value">
                {appreciationDividendInfo?.totalNetValue ?? '-'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>
    </div>
  )
}
